import { Component, Input, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ProfilePagesComponent } from '../profile-pages/widgets/profile-pages/profile-pages.component';
import { ProfileMenuComponent } from '../profile-pages/widgets/profile-menu/profile-menu.component';
import { FriendsTitleComponent } from '../profile-pages/widgets/about-friend-list/friends-title/friends-title.component';
import { ProfileFriendsSkeletonComponent } from '../../shared/skeleton-loader/profile-pages-skeleton/profile-friends-skeleton/profile-friends-skeleton.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FeatherIconComponent } from '../../shared/components/common/feather-icon/feather-icon.component';
import { FormControl, ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { friends } from '../../shared/data/profile-pages/about';
import { CommonService } from '../../shared/services/common.service';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { HeaderOneComponent } from '../../shared/components/common/header/header-one/header-one.component';
import { EntityService } from '../../services/entity.service';
import { encodeId, decodeId } from '../../helpers/utils';
import { CelebrityByDomainSkeletonComponent } from './celebrity-by-domain-skeleton.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../services/auth.service';
import { FollowService } from "../../services/follow.service";
import { ImageViewerModalComponent } from "../../components/image-viewer-modal/image-viewer-modal.component";
import { SeoService } from '../../services/seo.service';
import { AuthModalService } from '../../shared/services/auth-modal.service';
import { SecureStorageService } from '../../services/secure-storage.service';

@Component({
  selector: 'app-celebrity-by-domain',
  standalone: true,
  imports: [ProfilePagesComponent, ProfileMenuComponent, FriendsTitleComponent,
    ProfileFriendsSkeletonComponent, RouterModule, CommonModule,
    FeatherIconComponent, ReactiveFormsModule, HeaderOneComponent, CelebrityByDomainSkeletonComponent],
  templateUrl: './celebrity-by-domain.component.html',
  styleUrl: './celebrity-by-domain.component.scss'
})
export class CelebrityByDomainComponent implements OnInit, OnDestroy {
  public storyData: any[] = []; // Données complètes
  public filteredData: any[] = []; // Données filtrées
  public isShow: boolean = false;
  public friends = friends;
  public domain: any;
  id: string | null = null;
  @Input() currentUrl: string;

  // ✅ Déclaration du champ de recherche
  searchControl: FormControl;
  domainControl: FormControl; // Nouveau contrôle pour le sélecteur
  countryControl: FormControl; // Nouveau contrôle pour le filtre pays
  domains: any[] = []; // Liste des domaines pour le sélecteur
  countries: any[] = []; // Liste des pays pour le filtre
  isLoadingCelebrities: boolean = false; // Indicateur de chargement
  isLoading: boolean = false; // Pour les chargements suivants (scroll)
  isInitialLoading: boolean = true; // Pour afficher le skeleton au premier chargement
  currentPage: number = 1;
  hasMore: boolean = false;

  private dataLoaded = {
    domain: false,
    celebrities: false,
    domainsList: false, // Nouveau flag
    countriesList: false // Nouveau flag pour les pays
  };

  // Follow tracking
  public followedIds: Set<number> = new Set();
  public sortControl: FormControl;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private entityService: EntityService,
    public commonServices: CommonService,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private authService: AuthService,
    private followService: FollowService,
    private seoService: SeoService,
    private authModalService: AuthModalService,
    private secureStorage: SecureStorageService
  ) {
    this.commonServices.setSkeletonState(false);
    this.searchControl = this.formBuilder.control('');
    this.domainControl = this.formBuilder.control('Tous');
    this.countryControl = this.formBuilder.control('Tous');
    this.sortControl = this.formBuilder.control('popularity');
  }

  ngOnInit() {
    // Empêcher le scroll pendant le chargement initial
    if (this.isInitialLoading) {
      document.body.style.overflow = 'hidden';
    }
    
    this.loadAllDomains();
    this.loadAllCountries();
    this.loadAllData();
    this.loadFollowedCelebrities();
    this.setupSearch();
    this.setupDomainFilter();
    this.setupCountryFilter();
    this.setupSortFilter();
  }

  private loadAllDomains() {
    this.entityService.getData('/domains').subscribe({
      next: (data: any) => {
        this.domains = data.data || data; // Adapter selon la structure de réponse
        this.dataLoaded.domainsList = true;
        this.checkAllDataLoaded();
      },
      error: () => {
        this.dataLoaded.domainsList = true;
      }
    });
  }

  private loadAllCountries() {
    this.authService.getNationnalities().subscribe({
      next: (data: any) => {
        this.countries = data.data || data; // Adapter selon la structure de réponse

        // Ne pas définir de pays par défaut - laisser l'utilisateur choisir
        // Le pays reste à "Tous" par défaut

        // Re-appliquer les filtres si les célébrités sont déjà chargées
        if (this.storyData?.length) {
          this.applyFilters();
        }

        this.dataLoaded.countriesList = true;
        this.checkAllDataLoaded();
      },
      error: () => {
        this.dataLoaded.countriesList = true;
      }
    });
  }

  private loadAllData() {
    this.commonServices.setSkeletonState(false);

    // Charger le domaine courant via l'URL
    this.route.params.subscribe(params => {
      const encodedId = params['id'];

      // Décoder l'ID depuis l'URL
      const domainId = decodeId(encodedId);

      // Mettre à jour la valeur du sélecteur sans déclencher l'événement change (emitEvent: false)
      // pour éviter une boucle infinie ou un double chargement au démarrage
      if (domainId) {
        this.domainControl.setValue(domainId, { emitEvent: false });
        this.loadDomainDetails(domainId);
      }
    });
  }

  private loadDomainDetails(domainId: any) {
    this.storyData = [];
    this.filteredData = [];
    this.isLoadingCelebrities = true;

    this.entityService.getDataById('/domains', domainId).subscribe({
      next: (data: any) => {
        if (data) {
          this.domain = data;
          const domainName = data.name || 'Célébrités';
          this.seoService.updateSeo({
            title: `Célébrités – ${domainName}`,
            description: `Découvrez les célébrités du domaine ${domainName} sur YokaStar. Demandez une vidéo dédicacée personnalisée à vos stars préférées.`,
            keywords: `${domainName}, célébrités, vidéos dédicacées, YokaStar, messages personnalisés`,
            url: `https://yokastar.com${this.router.url}`
          });
          this.seoService.setStructuredData({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            'name': `Célébrités ${domainName} – YokaStar`,
            'description': `Découvrez toutes les célébrités du domaine ${domainName} disponibles sur YokaStar.`,
            'url': `https://yokastar.com${this.router.url}`,
            'isPartOf': { '@type': 'WebSite', 'name': 'YokaStar', 'url': 'https://yokastar.com/' }
          });
          this.loadCelebrities(1);
        }
        this.dataLoaded.domain = true;
        this.checkAllDataLoaded();
      },
      error: () => {
        this.dataLoaded.domain = true;
        this.isLoadingCelebrities = false;
        this.checkAllDataLoaded();
      }
    });
  }

  ngOnDestroy(): void {
    this.seoService.removeStructuredData();
  }

  loadCelebrities(page: number = 1) {
    if (this.isLoading) return;

    if (page === 1) {
      this.isLoadingCelebrities = true;
    }
    this.isLoading = true;

    // Récupérer les filtres actuels
    const filters = {
      search: this.searchControl.value,
      domain_id: this.domainControl.value,
      nationalite_id: this.countryControl.value,
      sort: this.sortControl.value
    };

    this.commonServices.getAllCelebrities(page, filters).subscribe({
      next: (response) => {
        if (response && response.data) {
          if (page === 1) {
            this.storyData = response.data;
            this.filteredData = response.data;
          } else {
            // Éviter les doublons
            const newItems = response.data.filter(
              (newItem: any) => !this.storyData.some((oldItem: any) => oldItem.id === newItem.id)
            );
            this.storyData = [...this.storyData, ...newItems];
            this.filteredData = [...this.storyData];
          }

          this.currentPage = response.pagination?.current_page || page;
          this.hasMore = response.pagination?.has_more || false;
        }

        this.isLoading = false;
        this.isLoadingCelebrities = false;
        this.isInitialLoading = false; // Désactiver le skeleton après le premier chargement
        
        // Réactiver le scroll
        document.body.style.overflow = '';
        
        this.dataLoaded.celebrities = true;
        this.checkAllDataLoaded();
      },
      error: (err) => {
        this.isLoading = false;
        this.isLoadingCelebrities = false;
        this.isInitialLoading = false; // Désactiver le skeleton même en cas d'erreur
        
        // Réactiver le scroll
        document.body.style.overflow = '';
        
        this.dataLoaded.celebrities = true;
        this.checkAllDataLoaded();
        console.error("Erreur lors du chargement des célébrités", err);
      }
    });
  }

  private setupDomainFilter() {
    this.domainControl.valueChanges.subscribe(domainId => {
      if (domainId) {
        // Encoder l'ID avant de naviguer vers la nouvelle URL
        const encodedId = encodeId(domainId);
        this.router.navigate(['/celebrities/domaines', encodedId]);
      }
    });
  }

  private checkAllDataLoaded() {
    if (Object.values(this.dataLoaded).every(value => value === true)) {
      setTimeout(() => {
        this.commonServices.setSkeletonState(true);
      }, 500);
    }
  }

  private setupSearch() {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.applyFilters();
    });
  }

  private setupCountryFilter() {
    this.countryControl.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  private setupSortFilter() {
    this.sortControl.valueChanges.subscribe(() => {
      this.applyFilters();
    });
  }

  private applyFilters() {
    this.currentPage = 1;
    this.loadCelebrities(1);
  }

  @HostListener("window:scroll", ["$event"])
  onScroll(event: Event) {
    const scrollPosition = window.pageYOffset + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    // Charger plus de données quand on atteint 80% de la page
    if (scrollPosition >= pageHeight * 0.8 && this.hasMore && !this.isLoading) {
      this.loadCelebrities(this.currentPage + 1);
    }
  }

  loadMore() {
    if (this.hasMore && !this.isLoading) {
      this.loadCelebrities(this.currentPage + 1);
    }
  }



  // ✅ Réinitialiser tous les filtres
  resetFilters() {
    this.searchControl.setValue("");
    this.domainControl.setValue("Tous");
    this.countryControl.setValue("Tous");
    this.sortControl.setValue("popularity");
  }

  getSelectedCountryFlagUrl(): string {
    const selectedId = this.countryControl.value;
    const selected = (this.countries || []).find((c: any) => String(c?.id) === String(selectedId));
    const flagPath = selected?.flag && String(selected.flag).trim() ? String(selected.flag).trim() : 'flags/cd.svg';
    return `assets/svg/${flagPath}`;
  }

  getSelectedCountryName(): string {
    const selectedId = this.countryControl.value;
    const selected = (this.countries || []).find((c: any) => String(c?.id) === String(selectedId));
    return selected?.name || '';
  }

  loadFollowedCelebrities() {
    this.followService.getFollowedCelebrityIds().subscribe({
      next: (ids) => {
        this.followedIds = new Set(ids);
      },
      error: (err) => console.error('Erreur chargement célébrités suivies', err)
    });
  }

  isFollowing(celebrity: any): boolean {
    if (celebrity && celebrity.is_following !== undefined) {
      return !!celebrity.is_following;
    }
    return this.followedIds.has(Number(celebrity.id));
  }

  private isUserAuthenticated(): boolean {
    const raw = this.secureStorage.getItem<string>('token');
    if (!raw) return false;
    const normalized = String(raw).replace(/"/g, '').trim();
    return normalized.length > 0 && normalized !== 'null' && normalized !== 'undefined';
  }

  toggleFollow(celebrity: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isUserAuthenticated()) {
      this.authModalService.openAuthModal();
      return;
    }

    const celebrityId = Number(celebrity.id);
    const isCurrentlyFollowing = this.isFollowing(celebrity);

    // ✅ Mise à jour optimiste (immédiate)
    if (isCurrentlyFollowing) {
      this.followedIds.delete(celebrityId);
      celebrity.is_following = false;
      if (celebrity.followers_count > 0) celebrity.followers_count--;

      this.followService.unfollow(celebrityId).subscribe({
        next: () => {
          // Déjà mis à jour, rien à faire de plus si succès
        },
        error: (err) => {
          console.error('Erreur lors du désabonnement', err);
          // ❌ Revenir en arrière en cas d'erreur
          this.followedIds.add(celebrityId);
          celebrity.is_following = true;
          celebrity.followers_count++;
        }
      });
    } else {
      this.followedIds.add(celebrityId);
      celebrity.is_following = true;
      celebrity.followers_count = (Number(celebrity.followers_count) || 0) + 1;

      this.followService.follow(celebrityId).subscribe({
        next: () => {
          // Déjà mis à jour, rien à faire de plus si succès
        },
        error: (err) => {
          console.error('Erreur lors de l\'abonnement', err);
          // ❌ Revenir en arrière en cas d'erreur
          this.followedIds.delete(celebrityId);
          celebrity.is_following = false;
          if (celebrity.followers_count > 0) celebrity.followers_count--;
        }
      });
    }
  }

  // ✅ Formater le nombre d'abonnés (ex: 1.5K, 2M)
  formatFollowers(count: number): string {
    if (!count) return '0';
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (count >= 1000) {
      return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return count.toString();
  }

  openImagePreview(imageUrl: string) {
    const modalRef = this.modalService.open(ImageViewerModalComponent, {
      centered: true,
      size: 'lg',
      windowClass: 'image-viewer-modal'
    });
    modalRef.componentInstance.imageUrl = imageUrl;
  }

  getEncodedId(id: string | number): string {
    return encodeId(id);
  }

  handleImageError(event: any, item: any) {
    item._hideImage = true;
  }

  getUserInitial(name?: string): string {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  }

  getAvatarColor(name?: string): string {
    if (!name) return '#6b7280';
    const colors = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  // ✅ Obtenir l'icône selon le domaine
  getDomainIcon(domainName: string): string {
    if (!domainName) return 'star';
    const name = domainName.toLowerCase();
    if (name.includes('musique') || name.includes('music')) return 'music_note';
    if (name.includes('cinéma') || name.includes('movie') || name.includes('film')) return 'movie';
    if (name.includes('sport')) return 'sports_basketball';
    if (name.includes('comédie') || name.includes('humour')) return 'theater_comedy';
    if (name.includes('mode') || name.includes('fashion')) return 'checkroom';
    if (name.includes('danse')) return 'auto_awesome';
    return 'star';
  }
}
